import { createApi, fetchBaseQuery } from '@reduxjs/toolkit/query/react';
import store from 'store';
import getDashboardApiUrl from '@/utils/getDashboardApiUrl';
import type { NotificationSource, Notifications } from '@/components/notifications/notificationSettings';
import {
	CREATE_REMINDER,
	DELETE_REMINDER,
	UPDATE_REMINDER,
} from '@/actions/types';

const baseUrl = getDashboardApiUrl(window.location.href);

export type ReminderPriority = 'none' | 'low' | 'med' | 'high';
export type RecurrenceRule = 'none' | 'daily' | 'weekly' | 'monthly';
// Minutes before dateTime at which the at-due notification is delivered.
// null = no notification. Legacy docs may omit the field entirely (≙ 0) —
// treat undefined as 0 with an explicit check, never `?? 0` (which would
// also coerce null and silently re-enable notifications).
export type NotificationOffset = 0 | 5 | 15 | 30 | 60 | null;

export interface ReminderRecurrence {
	rule: RecurrenceRule;
	until?: string | null;
}

export interface ReminderCustomer {
	_id: string;
	name?: string;
	email?: string;
	phone?: string;
	facebookUserId?: string;
	googleConversationId?: string;
}

export interface ReminderAssignee {
	_id: string;
	name?: string;
	username?: string;
}

export type SequenceAction = 'call' | 'text' | 'email' | 'meeting' | 'todo';

/** Present when the reminder is a step of a customer's reminder sequence */
export interface ReminderSequenceStep {
	_reminderSequence: string;
	_enrollment: string;
	stepId: string;
	stepNumber: number;
	stepCount: number;
	action: SequenceAction;
	sequenceName?: string;
	editedAt?: string;
}

export interface Reminder {
	_id: string;
	/** Whoever created it; not populated */
	_user?: string;
	text: string;
	notes?: string;
	dateTime: string;
	isCompleted: boolean;
	completedAt?: string | null;
	/** Who completed it; populated with name */
	_completedBy?: ReminderAssignee | string | null;
	sequence?: ReminderSequenceStep;
	priority: ReminderPriority;
	recurrence?: ReminderRecurrence;
	_customer?: ReminderCustomer | null;
	_assignee?: ReminderAssignee | null;
	_assignees?: ReminderAssignee[];
	notifiedAt?: string | null;
	snoozedUntil?: string | null;
	notifications?: NotificationSource['notifications'];
	notificationOffsetMinutes?: number | null;
	createdAt: string;
	updatedAt: string;
}

export interface RemindersQueryParams {
	status?: 'open' | 'completed' | 'all';
	_assignee?: string;
	_customer?: string;
	search?: string;
	fromDate?: string;
	toDate?: string;
	page?: number;
	limit?: number;
	kind?: 'all' | 'sequence' | 'oneoff';
	/** Also open reminders due before fromDate */
	includeOverdue?: boolean;
}

export interface RemindersListResponse {
	error: boolean;
	reminders: Reminder[];
	total: number;
	page: number;
	limit: number;
}

export interface CustomerRemindersResponse {
	error: boolean;
	reminders: Reminder[];
}

export interface SingleReminderResponse {
	error: boolean;
	reminder: Reminder;
	nextReminder?: Reminder | null;
}

export interface CreateReminderBody {
	text: string;
	dateTime: string;
	_customer?: string | null;
	_assignee?: string | null;
	/** Empty or missing assigns the reminder to whoever creates it */
	_assignees?: string[];
	notes?: string;
	priority?: ReminderPriority;
	recurrence?: ReminderRecurrence;
	notifications?: Notifications;
	notificationOffsetMinutes?: number | null;
}

export interface UpdateReminderBody {
	_id: string;
	updates: Partial<{
		text: string;
		dateTime: string;
		notes: string;
		priority: ReminderPriority;
		recurrence: ReminderRecurrence;
		notifications: Notifications;
		_customer: string | null;
		_assignee: string | null;
		_assignees: string[];
		isCompleted: boolean;
	}>;
}

export const LIST_TAG = { type: 'Reminder' as const, id: 'LIST' };
/** Customers' reminder sequence runs; a step's reminder changing changes these */
export const ENROLLMENT_LIST_TAG = { type: 'SequenceEnrollment' as const, id: 'LIST' };
export const SEQUENCE_LIST_TAG = { type: 'ReminderSequence' as const, id: 'LIST' };

type Dispatch = (action: { type: string; payload: unknown }) => void;

// The calendar reads reminders from the classic `reminders` store, so copy saved changes there too
const syncSavedReminder = async (
	{
		dispatch,
		queryFulfilled,
	}: {
		dispatch: Dispatch;
		queryFulfilled: Promise<{ data: SingleReminderResponse }>;
	},
	type: typeof CREATE_REMINDER | typeof UPDATE_REMINDER
) => {
	try {
		const { data } = await queryFulfilled;
		if (data?.reminder) dispatch({ type, payload: data.reminder });
		if (data?.nextReminder) {
			dispatch({ type: CREATE_REMINDER, payload: data.nextReminder });
		}
	} catch {
		// The page that made the change reports the failure
	}
};

export const remindersApi = createApi({
	reducerPath: 'remindersApi',
	baseQuery: fetchBaseQuery({
		baseUrl,
		prepareHeaders: (headers) => {
			const token = store.get('jwt');
			if (token) {
				headers.set('Authorization', `JWT ${token}`);
			}
			return headers;
		},
	}),
	tagTypes: ['Reminder', 'ReminderSequence', 'SequenceEnrollment'],
	endpoints: (builder) => ({
		getReminders: builder.query<RemindersListResponse, RemindersQueryParams>({
			query: (params) => ({ url: '/api/reminders', params }),
			providesTags: (result) =>
				result
					? [
							...result.reminders.map((r) => ({
								type: 'Reminder' as const,
								id: r._id,
							})),
							LIST_TAG,
						]
					: [LIST_TAG],
		}),
		getCustomerReminders: builder.query<
			CustomerRemindersResponse,
			string | { _customer: string; kind?: 'all' | 'sequence' | 'oneoff' }
		>({
			query: (arg) => {
				const { _customer, kind } =
					typeof arg === 'string' ? { _customer: arg, kind: undefined } : arg;
				return {
					url: `/api/reminders/customer/${_customer}`,
					params: kind ? { kind } : undefined,
				};
			},
			providesTags: (result) =>
				result
					? [
							...result.reminders.map((r) => ({
								type: 'Reminder' as const,
								id: r._id,
							})),
							LIST_TAG,
						]
					: [LIST_TAG],
		}),
		createReminder: builder.mutation<SingleReminderResponse, CreateReminderBody>(
			{
				query: (body) => ({
					url: '/api/reminders',
					method: 'POST',
					body,
				}),
				invalidatesTags: [LIST_TAG],
				onQueryStarted: (_body, api) =>
					syncSavedReminder(api, CREATE_REMINDER),
			}
		),
		updateReminder: builder.mutation<SingleReminderResponse, UpdateReminderBody>(
			{
				query: ({ _id, updates }) => ({
					url: `/api/reminders/${_id}`,
					method: 'PATCH',
					body: updates,
				}),
				invalidatesTags: (_result, _error, { _id }) => [
					{ type: 'Reminder', id: _id },
					LIST_TAG,
					ENROLLMENT_LIST_TAG,
					SEQUENCE_LIST_TAG,
				],
				onQueryStarted: (_body, api) =>
					syncSavedReminder(api, UPDATE_REMINDER),
			}
		),
		completeReminder: builder.mutation<
			SingleReminderResponse,
			{ _id: string; isCompleted?: boolean }
		>({
			query: ({ _id, isCompleted }) => ({
				url: `/api/reminders/${_id}/complete`,
				method: 'POST',
				body: typeof isCompleted === 'boolean' ? { isCompleted } : {},
			}),
			invalidatesTags: (_result, _error, { _id }) => [
				{ type: 'Reminder', id: _id },
				LIST_TAG,
				ENROLLMENT_LIST_TAG,
				SEQUENCE_LIST_TAG,
			],
			onQueryStarted: (_body, api) => syncSavedReminder(api, UPDATE_REMINDER),
		}),
		rescheduleReminder: builder.mutation<
			SingleReminderResponse,
			{ _id: string; dateTime?: string; minutes?: number }
		>({
			query: ({ _id, dateTime, minutes }) => ({
				url: `/api/reminders/${_id}/reschedule`,
				method: 'POST',
				body: { dateTime, minutes },
			}),
			invalidatesTags: (_result, _error, { _id }) => [
				{ type: 'Reminder', id: _id },
				LIST_TAG,
				ENROLLMENT_LIST_TAG,
				SEQUENCE_LIST_TAG,
			],
			onQueryStarted: (_body, api) => syncSavedReminder(api, UPDATE_REMINDER),
		}),
		deleteReminder: builder.mutation<
			{ error: boolean; _reminder: string },
			string
		>({
			query: (_id) => ({
				url: `/api/reminders/${_id}`,
				method: 'DELETE',
			}),
			invalidatesTags: (_result, _error, _id) => [
				{ type: 'Reminder', id: _id },
				LIST_TAG,
				ENROLLMENT_LIST_TAG,
				SEQUENCE_LIST_TAG,
			],
			onQueryStarted: async (_id, { dispatch, queryFulfilled }) => {
				try {
					await queryFulfilled;
					dispatch({ type: DELETE_REMINDER, payload: _id });
				} catch {
					// The page that made the change reports the failure
				}
			},
		}),
		bulkReminders: builder.mutation<
			{ error: boolean; ids: string[]; action: string },
			{ ids: string[]; action: 'complete' | 'delete' }
		>({
			query: (body) => ({
				url: '/api/reminders/bulk',
				method: 'POST',
				body,
			}),
			invalidatesTags: [LIST_TAG, ENROLLMENT_LIST_TAG, SEQUENCE_LIST_TAG],
			onQueryStarted: async ({ ids, action }, { dispatch, getState, queryFulfilled }) => {
				try {
					await queryFulfilled;
				} catch {
					return;
				}
				const calendarReminders: Reminder[] =
					(getState() as { reminders?: { reminders?: Reminder[] } }).reminders
						?.reminders || [];
				for (const _id of ids) {
					if (action === 'delete') {
						dispatch({ type: DELETE_REMINDER, payload: _id });
						continue;
					}
					const reminder = calendarReminders.find((r) => r._id === _id);
					if (reminder) {
						dispatch({
							type: UPDATE_REMINDER,
							payload: { ...reminder, isCompleted: true },
						});
					}
				}
			},
		}),
	}),
});

export const {
	useGetRemindersQuery,
	useGetCustomerRemindersQuery,
	useCreateReminderMutation,
	useUpdateReminderMutation,
	useCompleteReminderMutation,
	useRescheduleReminderMutation,
	useDeleteReminderMutation,
	useBulkRemindersMutation,
} = remindersApi;
